import { fn, CreateCounter, IInfOperation } from '../utils';
import FindFirst = fn.FindFirst;
import { Bind as kBind, NumericFormat, Bind, constants } from '../navkendo/kutils';
import { IPageObj, TDefaultPageObj, IPageMetaData, TPagePropertyAccess, IPageProperty } from '../nav/navtypes';
import { getClientKeyFunc, IObservableArray, Observable } from './navmodel';
import { NavPropertyItem } from './navpropertyitem';
import { NavItemAnchor } from './navitemanchor';
import { NavClientService } from './navclientservice';
import { NavPropertySelection } from './navpropertyselection';

/** Base Functionality of SubPage Items Container (NavLineData) and Top Level ReadMultiple Container (NavPageItems)) */
export abstract class NavPropertyItems<TPage extends IPageObj = TDefaultPageObj> extends Observable {
    constructor(readonly Service: NavClientService, public readonly MetaData: IPageMetaData<TPage>) {
        super();
        this.GetKey = getClientKeyFunc(MetaData);
    }
    /** Get Client Key for a ModelItem */
    public GetKey: (itemData: any, noerror?: boolean) => string;
    /** Resolve Property By Name /Property Instance to 'Owned' Property Instance, opt. throwing Error if not available */
    public ResolveProperty(prop: TPagePropertyAccess<TPage>, throwError?: boolean) {
        return this.MetaData.ResolveProperty(prop, throwError);
    }
    /** Reference to Root Read/Update (ReadMultiple/UpdateMultiple) */
    public Anchor: NavItemAnchor<IPageObj>;
    /** Array of Base Model Items */
    public abstract readonly ModelItems: IObservableArray<TPage>;
    /** Container of Underlying Items */
    public abstract readonly Items: Array<NavPropertyItem<TPage>>;
    /** Number of Currently Selected items */
    public SelectionCount: number = 0;
    /** Index of the First Selected Item (or -1 if none) */
    public FirstSelected: number = -1;
    /** Current Count of Items */
    public get Count() { return (this.Items || []).length; };
    /** Selected Client Keys */
    public SelectedIds: string[] = [];
    /** Selected Items */
    public readonly SelectedItems = new Array<NavPropertyItem<TPage>>();
    /** Functionality to Delete Items in persistence */
    public abstract DeleteItems(ids: string[]): PromiseLike<void>;
    /** Functionality to insert a new item */
    public abstract InsertNewItem(): PromiseLike<NavPropertyItem<TPage>>;
    /** Functionality to duplicate items */
    public abstract DuplicateItems(ids: string[]): PromiseLike<Array<NavPropertyItem<TPage>>>;

    /** Set Selected State of given items (id(s) or item(s)  */
    public SetSelected(items: any, prop?: string | IPageProperty) {
        // avoid reentrancy
        var counter = this["_selCounter"] || (this["_selCounter"] = CreateCounter(this));

        // 2 : Allow indirect setting of Selection as reaction of Selection Changed
        if (!counter.IsRunning(2)) {
            counter.Wrap(() => {
                var newItems = this.GetItems(items);
                var newItemKeys = newItems.map(item => item.ClientKey);
                var hasChange: boolean;
                if (newItemKeys.length != this.SelectedIds.length || newItemKeys.sort().join(",") != this.SelectedIds.sort().join(",")) {
                    hasChange = true;
                    this.SelectedIds = newItemKeys;
                    this.SelectionCount = newItemKeys.length;
                    this.FirstSelected = this.SelectionCount == 0 ? -1 : this.Items.indexOf(this.GetItems(newItemKeys[0])[0]);
                    this.SelectedItems.length = 0;
                    this.SelectedItems.push.apply(this.SelectedItems, newItems);
                }

                prop = prop && this.ResolveProperty(<any>prop, false);
                if (prop && prop !== this.SelectedProperty) {
                    hasChange = true;
                    this.SelectedProperty = prop;
                }

                if (hasChange) {
                    this.refreshSelectionInfo();
                    this.trigger("selectionchanged", { LineData: this, SelectedProp: this.SelectedProperty });
                }
            });
        }
    }

    public SelectedProperty: IPageProperty;

    /** Aggregated Selection Info for Current Context */
    public SelectionInfo: string;

    /** Current SelectionInfo */
    public CurrentSelectionInfo: NavPropertySelection;

    /** Paster Current Selection to these items */
    public get PasteSelectionAction() {
        if (!this._pasteSelectionAction) {
            const p = this._pasteSelectionAction = {
                Enabled: NavPropertySelection.Current.Count > 0,
                Title: 'Kopierte Zellen einfügen',
                Hidden: "Enabled",
                Execute: () => {
                    const selInfo = this.CurrentSelectionInfo;
                    const items = this.SelectedItems;
                    let totalAmounts: number[];
                    if (!selInfo || !selInfo.Prop || !(items.length > 0)) {
                        throw new Error('keine Ziel- Selektion zum Einfügen');
                    }
                    const source = NavPropertySelection.Current;
                    if (!source || !source.Prop || !(source.Count > 0)) {
                        throw new Error('keine Quell- Selektion zum Einfügen');
                    }
                    const sourceValues = source.GetValues(selInfo.Prop);

                    const Anchor = this.Anchor;
                    const $c = Anchor.Service.$c;

                    if (source.Count == items.length) {
                        const setOnClient = fn.ToArray(items).reduce((p, item, idx) => {
                            return $c.when(p).then(() => item.SetValue(selInfo.Prop, sourceValues[idx], false));
                        }, $c.when<any>());
                        return $c.when(setOnClient).then(() => Anchor.Update());
                    } else if (items.length == 1 && selInfo.Prop.Type == "String") {
                        // n * text -> 1 * text
                        let value = fn.ToArray(sourceValues).map(item => "" + item).join(" ");
                        const shortened = selInfo.Prop.MaxLength > 0 && value.length > selInfo.Prop.MaxLength;
                        if (shortened) {
                            value = value.substr(0, selInfo.Prop.MaxLength);
                            var ask = $c.resolve("DialogService").ShowConfirm("Eingabe wird gekürzt, trotzdem fortfahen?");
                        }
                        return $c.when(ask).then(r => r !== false && items[0].SetValue(selInfo.Prop, value));
                    } else if (items.length == 1 && selInfo.Prop.Type == "Decimal") {
                        // n * decimal -> 1 * decimal
                        let value = sourceValues.reduce((p, c) => p + c, 0.0);
                        return items[0].SetValue(selInfo.Prop, value);
                    } else if (source.Count == 1 && source.Prop.Type == "Decimal" && selInfo.Prop.Type == "Decimal"
                        && (totalAmounts = fn.splitAmount(sourceValues[0], items.length))) {
                        // Verteilen des einen Betrags auf die Zeilen
                        return items.reduce((p, c, i) => p.then(() => c.SetValue(selInfo.Prop, totalAmounts[i], false)),
                            $c.when<any>()).then(() => Anchor.Update());
                    } else {
                        throw new Error('Quell/Zielselektion stimmen nicht überein!');
                    }
                }
            };

            // Setup Refresh of Action
            const refreshEnabled = () => {
                const curr = this.CurrentSelectionInfo;
                const selItems = this.SelectedItems;
                p.Enabled = NavPropertySelection.Current.Count > 0 && curr &&
                    selItems.length > 0 && curr.Prop && !curr.Prop.ReadOnly && !this.Anchor.Readonly.GetValue(curr.Prop, selItems[0]);
            };
            this.one(constants.NavDestroy, Bind(NavPropertySelection.Current, refreshEnabled, 'change'));
            this.one(constants.NavDestroy, Bind(this, refreshEnabled, 'selectionchanged'));
        }
        return this._pasteSelectionAction;

    }
    private _pasteSelectionAction /** : IInfOperation **/;

    /*  Store Current Object Selection as Global Selection*/
    public get CopySelectionAction() {
        if (!this._copySelectionAction) {
            const copyTitles = { ONE: 'Zelle kopieren', M: 'Zellen kopieren' };
            const actCopyCurrentRowContext = this._copySelectionAction = {
                Enabled: true,
                Title: copyTitles.ONE,
                Hint: '',
                Execute: () => {
                    this.CurrentSelectionInfo.SetAsAcurrent();
                }
            };

            const refreshTitle = () => {
                const selInfo = this.CurrentSelectionInfo;
                actCopyCurrentRowContext.Title = selInfo && selInfo.Count > 1 ? copyTitles.M : copyTitles.ONE;
                if (selInfo && selInfo.Count > 1)
                    actCopyCurrentRowContext.Hint = `${selInfo.Count} Zellen ('${selInfo.Prop.Title}') kopieren'`;
                else
                    actCopyCurrentRowContext.Hint = "";
            };
            this.one(constants.NavDestroy, Bind(this, refreshTitle, 'selectionchanged'));
            refreshTitle();
        }
        return this._copySelectionAction;
    }
    private _copySelectionAction: IInfOperation & { Title: string, Hint: string };

    refreshSelectionInfo() {
        const SelectedProperty = this.SelectedProperty;
        let selItems = this.SelectedItems;
        const getValues = () => SelectedProperty && selItems && selItems.map(item => item.GetValue(SelectedProperty));

        const selectionInfo = this.CurrentSelectionInfo = new NavPropertySelection(SelectedProperty, getValues());

        if (!(selectionInfo.Count > 1)) {
            this.SelectionInfo = "";
            return;
        }

        switch (SelectedProperty.Type) {
            case "Decimal":
                const sum = this.SelectedItems.reduce((p, item) => p + <number><any>item.GetValue(SelectedProperty), 0.0);
                const format = NumericFormat[SelectedProperty.DecimalNumericType];
                this.SelectionInfo = `Summe '${SelectedProperty.Title}' selektiert: ${format ? kendo.format(format, sum) : sum}`;
                break;
            default:
                this.SelectionInfo = "";
                break;
        }
    }

    /** Register Notification of Selection Change */
    RegisterSelectionChanged(callback: () => void) {
        return kBind(this, callback, "selectionchanged");
    }

    /** Get Items : by id, number, NavPropertyItem, Model, or array of such */
    public GetItems(idOrObjOrArray: any): Array<NavPropertyItem<TPage>> {
        const Items = this.Items;
        const getItem = ((id: any) => {
                if (typeof id == "number")
                    return Items[id];

                return FindFirst(Items, item => item.IsMatch(id));
            });

        if ($.isArray(idOrObjOrArray)) {
            return idOrObjOrArray.map(x => getItem(x)).filter(item => !!item);
        } else
            return [getItem(idOrObjOrArray)].filter(item => !!item);
    }

    /** Internal Remove Deleted Item */
    NotifyDeleted(clientKey: string) {
        let Items = this.Items,
            toDelete = FindFirst(Items, item => item.ClientKey == clientKey);
        if (!toDelete)
            throw new Error("Failed to find Item to delete " + clientKey);
        var idx = Items.indexOf(toDelete);
        Items.splice(idx, 1);
        var selected = this.SelectedIds.indexOf(clientKey);
        if (selected >= 0) {
            var ids = fn.ToArray(this.SelectedIds);
            ids.splice(selected, 1);
            this.SetSelected(ids);
        }

        this.trigger("countchanged");
        return idx;
    }

    /** Internal : Add New Item */
    NotifyAdded(items: Array<NavPropertyItem<TPage>>) {
        let Items = this.Items;
        Items.push(...items);
        this.trigger("countchanged");
    }

    /** React on Count Changed (+ initial)  **/
    public RegisterCountChanged(cb: () => any) {
        cb();
        return kBind(this, cb, "countchanged");
    }

    /** Navigate selected item */
    public Move(step: number) {
        var Items = this.Items,
            SelectedIds = this.SelectedIds;
        if (Items.length == 0)
            return;
        if (!SelectedIds[0]) {
            this.SetSelected(Items[0]);
            return;
        }
        var currSel = SelectedIds[0] && Items.indexOf(<any>this.GetItems(SelectedIds[0])[0]);
        var toSel = currSel < 0 ? this.Count - 1 : currSel + step;
        if (toSel < 0) toSel = 0;
        if (toSel >= Items.length)
            toSel = Items.length - 1;

        this.SetSelected(Items[toSel]);
    }
}
